'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { BadgeCheck, Check, X, Loader2 } from 'lucide-react'
import { apiService, FollowRequest } from '@/lib/api'
import { getMediaUrl } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface FollowRequestsProps {
  userId: number
  onRequestUpdate?: () => void
}

export default function FollowRequests({ userId, onRequestUpdate }: FollowRequestsProps) {
  const [requests, setRequests] = useState<FollowRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState<number | null>(null)
  const { toast } = useToast()

  // Get token from localStorage
  const getToken = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token')
    }
    return null
  }

  // Safety check: Ensure requests is always an array
  useEffect(() => {
    if (!Array.isArray(requests)) {
      console.warn('Requests state is not an array, resetting to empty array:', requests)
      setRequests([])
    }
  }, [requests])

  useEffect(() => {
    const fetchRequests = async () => {
      const token = getToken()
      if (!token) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const followRequests = await apiService.getFollowRequests(userId, token)
        
        // Handle different response formats
        let safeRequests: FollowRequest[] = []
        
        // Log for debugging
        console.log('Follow requests response:', followRequests, 'Type:', typeof followRequests, 'IsArray:', Array.isArray(followRequests))
        
        if (Array.isArray(followRequests)) {
          // Direct array response
          safeRequests = followRequests.filter((req: unknown) => req && typeof req === 'object') as FollowRequest[]
        } else if (followRequests && typeof followRequests === 'object' && followRequests !== null) {
          // Check if it's a paginated response or object with results/data
          const requestsData = followRequests as any
          if (Array.isArray(requestsData.results)) {
            safeRequests = requestsData.results.filter((req: unknown) => req && typeof req === 'object') as FollowRequest[]
          } else if (Array.isArray(requestsData.data)) {
            safeRequests = requestsData.data.filter((req: unknown) => req && typeof req === 'object') as FollowRequest[]
          } else if (Array.isArray(requestsData.follow_requests)) {
            safeRequests = requestsData.follow_requests.filter((req: unknown) => req && typeof req === 'object') as FollowRequest[]
          } else {
            // Try to find any array property
            const allValues = Object.values(requestsData)
            const arrayValues = allValues.filter((val) => Array.isArray(val))
            if (arrayValues.length > 0) {
              safeRequests = (arrayValues[0] as any[]).filter((req: unknown) => req && typeof req === 'object') as FollowRequest[]
            }
          }
        }
        
        // Ensure we always have an array - double check
        const finalRequests = Array.isArray(safeRequests) ? safeRequests : []
        console.log('Setting requests to:', finalRequests, 'Length:', finalRequests.length)
        setRequests(finalRequests)
      } catch (error) {
        console.error('Error fetching follow requests:', error)
        // Set empty array on error to prevent crashes
        setRequests([])
        toast({
          title: "Error",
          description: "Failed to load follow requests",
          variant: "destructive"
        })
      } finally {
        setLoading(false)
      }
    }

    fetchRequests()
  }, [userId, toast])

  const handleAccept = async (requestId: number) => {
    const token = getToken()
    if (!token) return

    setProcessingId(requestId)
    try {
      await apiService.acceptFollowRequest(requestId, token)
      setRequests(prev => {
        // Ensure prev is always an array
        const safePrev = Array.isArray(prev) ? prev : []
        return safePrev.filter(req => req && req.id !== requestId)
      })
      onRequestUpdate?.()
      toast({
        title: "Request accepted",
        description: "You are now following this user",
      })
    } catch (error) {
      console.error('Error accepting follow request:', error)
      toast({
        title: "Error",
        description: "Failed to accept follow request",
        variant: "destructive"
      })
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (requestId: number) => {
    const token = getToken()
    if (!token) return

    setProcessingId(requestId)
    try {
      await apiService.rejectFollowRequest(requestId, token)
      setRequests(prev => {
        // Ensure prev is always an array
        const safePrev = Array.isArray(prev) ? prev : []
        return safePrev.filter(req => req && req.id !== requestId)
      })
      onRequestUpdate?.()
      toast({
        title: "Request rejected",
        description: "Follow request has been rejected",
      })
    } catch (error) {
      console.error('Error rejecting follow request:', error)
      toast({
        title: "Error",
        description: "Failed to reject follow request",
        variant: "destructive"
      })
    } finally {
      setProcessingId(null)
    }
  }

  if (loading) {
    return null
  }

  // Ensure requests is always an array before using .map
  // Add extra safety check
  const safeRequests = (() => {
    if (!requests) return []
    if (Array.isArray(requests)) {
      return requests.filter(req => req && typeof req === 'object')
    }
    console.warn('Requests is not an array:', requests, typeof requests)
    return []
  })()

  if (safeRequests.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Follow Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-zinc-400">
            <p>No pending follow requests</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-white">Follow Requests ({safeRequests.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {safeRequests && Array.isArray(safeRequests) && safeRequests.length > 0 ? (
            safeRequests.map((request) => {
              if (!request || !request.id) {
                console.warn('Invalid request object:', request)
                return null
              }
              return (
              <div key={request.id} className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src={request.follower.profilePicturePath ? getMediaUrl(request.follower.profilePicturePath) : undefined}
                    alt={request.follower.user.username}
                  />
                  <AvatarFallback className="bg-zinc-700 text-white">
                    {request.follower.user.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-white">{request.follower.user.username}</p>
                    {request.follower.verified && (
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 flex-shrink-0">
                        <BadgeCheck className="h-3 w-3 text-white" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400">
                    {request.follower.followers_count} followers
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAccept(request.id)}
                  disabled={processingId === request.id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {processingId === request.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(request.id)}
                  disabled={processingId === request.id}
                >
                  {processingId === request.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
              )
            }).filter(Boolean)
          ) : (
            <div className="text-center py-8 text-zinc-400">
              <p>No pending follow requests</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
